import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLSecurity = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Security & Best Practices</div>
                <h1>HTML Security Best Practices</h1>
                <p className="hero-lead">
                    Protect your websites from common security vulnerabilities. Learn about XSS, CSRF,
                    clickjacking, Content Security Policy, and security headers for safer web applications.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Web Security Matters</h2>
                <p>
                    Web security vulnerabilities can lead to data breaches, stolen user information,
                    defaced websites, and loss of trust. Understanding HTML security is the first line of defense.
                </p>

                <div className="security-stats">
                    <div className="stat-card">
                        <h3>43%</h3>
                        <p>of cyber attacks target small businesses</p>
                    </div>
                    <div className="stat-card">
                        <h3>$4.35M</h3>
                        <p>average cost of a data breach (2022)</p>
                    </div>
                    <div className="stat-card">
                        <h3>68%</h3>
                        <p>of breaches take months to discover</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Cross-Site Scripting (XSS)</h2>
                <p>
                    <strong>XSS</strong> attacks inject malicious scripts into web pages viewed by other users.
                </p>

                <h3>Types of XSS:</h3>
                <div className="xss-types">
                    <div className="type-card">
                        <h4>1. Reflected XSS</h4>
                        <p>Malicious script comes from current HTTP request</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Vulnerable code --&gt;
<p>Search results for: <?php echo $_GET['q']; ?></p>

<!-- Attacker sends: --&gt;
example.com/search?q=<script>alert('XSS')</script>`} />
                        </div>
                    </div>

                    <div className="type-card">
                        <h4>2. Stored XSS</h4>
                        <p>Malicious script stored in database</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Vulnerable: User comment stored without sanitization --&gt;
<div class="comment">
    <script>/* Malicious code */</script>
</div>`} />
                        </div>
                    </div>

                    <div className="type-card">
                        <h4>3. DOM-based XSS</h4>
                        <p>Vulnerability in client-side JavaScript</p>
                        <div className="code-example">
                            <CodeBlock language="javascript" code={`// Vulnerable JavaScript
const name = new URLSearchParams(window.location.search).get('name');
document.getElementById('welcome').innerHTML = \`Hello \${name}\`;

// Attacker uses:
// example.com?name=<img src=x onerror=alert('XSS')>`} />
                        </div>
                    </div>
                </div>

                <h3>Prevention: Escape User Input</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span> Vulnerable (Using innerHTML)</span>
                    </div>
                    <CodeBlock language="javascript" code={`// NEVER do this with user input!
const userInput = getUserInput();
element.innerHTML = userInput; // XSS vulnerability!

document.write(userInput); // Also vulnerable!`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Safe (Using textContent)</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Use textContent to escape HTML
const userInput = getUserInput();
element.textContent = userInput; // Safe! Auto-escapes HTML

// Or create text nodes
const textNode = document.createTextNode(userInput);
element.appendChild(textNode);`} />
                </div>

                <h3>Sanitize HTML Input</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Use DOMPurify library for HTML sanitization
import DOMPurify from 'dompurify';
import { Warning } from '../../components/LessonUI';

const userHTML = getUserInput();
const cleanHTML = DOMPurify.sanitize(userHTML);
element.innerHTML = cleanHTML; // Now safe

// Or use template literals with escaping
function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

const safe = escapeHTML(userInput);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Content Security Policy (CSP)</h2>
                <p>
                    CSP helps prevent XSS by restricting what resources can be loaded:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>CSP Meta Tag</span>
                    </div>
                    <CodeBlock language="html" code={`<meta http-equiv="Content-Security-Policy" 
      content="default-src 'self'; 
               script-src 'self' https://trusted-cdn.com; 
               style-src 'self' 'unsafe-inline'; 
               img-src 'self' data: https:; 
               font-src 'self' https://fonts.gstatic.com; 
               connect-src 'self' https://api.example.com; 
               frame-ancestors 'none';">`} />
                </div>

                <h3>CSP Directives:</h3>
                <div className="csp-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Directive</th>
                                <th>Purpose</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>default-src</code></td>
                                <td>Fallback for other directives</td>
                                <td><code>'self'</code></td>
                            </tr>
                            <tr>
                                <td><code>script-src</code></td>
                                <td>Valid sources for JavaScript</td>
                                <td><code>'self' cdn.com</code></td>
                            </tr>
                            <tr>
                                <td><code>style-src</code></td>
                                <td>Valid sources for stylesheets</td>
                                <td><code>'self' 'unsafe-inline'</code></td>
                            </tr>
                            <tr>
                                <td><code>img-src</code></td>
                                <td>Valid sources for images</td>
                                <td><code>'self' https:</code></td>
                            </tr>
                            <tr>
                                <td><code>frame-ancestors</code></td>
                                <td>Who can embed this page</td>
                                <td><code>'none'</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Warning><strong> Avoid 'unsafe-inline' and 'unsafe-eval':</strong> These directives
                    significantly weaken CSP protection. Use nonces or hashes instead.</Warning>

                <h3>CSP with Nonces</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Server generates unique nonce per request --&gt;
<meta http-equiv="Content-Security-Policy" 
      content="script-src 'nonce-random123abc'">

<!-- Only scripts with matching nonce execute --&gt;
<script nonce="random123abc">
    // This script will execute
    console.log('Allowed!');
</script>

<script>
    // This script will be blocked (no nonce)
    console.log('Blocked!');
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Clickjacking Protection</h2>
                <p>
                    Prevent your site from being embedded in malicious iframes:
                </p>

                <h3>1. X-Frame-Options Header</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Via meta tag (limited support) --&gt;
<meta http-equiv="X-Frame-Options" content="DENY">

<!-- Options: --&gt;
<!-- DENY - Cannot be framed at all --&gt;
<!-- SAMEORIGIN - Can only be framed by same origin --&gt;
<!-- ALLOW-FROM https://example.com - Specific origin (deprecated) --&gt;`} />
                </div>

                <h3>2. CSP frame-ancestors</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Prevent all framing --&gt;
<meta http-equiv="Content-Security-Policy" 
      content="frame-ancestors 'none'">

<!-- Allow same origin --&gt;
<meta http-equiv="Content-Security-Policy" 
      content="frame-ancestors 'self'">

<!-- Allow specific origins --&gt;
<meta http-equiv="Content-Security-Policy" 
      content="frame-ancestors 'self' https://trusted-site.com">`} />
                </div>

                <h3>3. JavaScript Frame-Busting (Fallback)</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`<script>
// Prevent page from being framed
if (window.top !== window.self) {
    window.top.location = window.self.location;
}
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Cross-Site Request Forgery (CSRF)</h2>
                <p>
                    CSRF tricks users into performing unwanted actions while authenticated:
                </p>

                <h3>Attack Example:</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Malicious site creates hidden form --&gt;
<form action="https://bank.com/transfer" method="POST">
    <input type="hidden" name="amount" value="1000">
    <input type="hidden" name="to" value="attacker-account">
</form>
<script>
    // Auto-submit when victim visits page
    document.forms[0].submit();
</script>`} />
                </div>

                <h3>Prevention: CSRF Tokens</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Server generates unique token per session --&gt;
<form action="/transfer" method="POST">
    <input type="hidden" name="csrf_token" value="unique-token-123">
    <input type="text" name="amount">
    <button type="submit">Transfer</button>
</form>

<!-- Server validates token matches session before processing --&gt;`} />
                </div>

                <h3>SameSite Cookies</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Set cookies with SameSite attribute --&gt;
Set-Cookie: sessionid=abc123; SameSite=Strict; Secure; HttpOnly

<!-- SameSite options: --&gt;
<!-- Strict - Cookie not sent on any cross-site request --&gt;
<!-- Lax - Cookie sent on top-level navigation (default) --&gt;
<!-- None - Cookie sent on all requests (requires Secure) --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Secure Forms</h2>

                <h3>1. Use HTTPS</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Always use HTTPS for forms with sensitive data --&gt;
<form action="https://example.com/login" method="POST">
    <input type="email" name="email" required>
    <input type="password" name="password" required autocomplete="current-password">
    <button type="submit">Login</button>
</form>`} />
                </div>

                <h3>2. Autocomplete Attributes</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Control autocomplete for sensitive fields --&gt;
<form>
    <!-- Enable autocomplete for username --&gt;
    <input type="text" name="username" autocomplete="username">
    
    <!-- Enable for current password --&gt;
    <input type="password" name="password" autocomplete="current-password">
    
    <!-- For new password --&gt;
    <input type="password" name="new-password" autocomplete="new-password">
    
    <!-- Disable for sensitive fields --&gt;
    <input type="text" name="credit-card" autocomplete="off">
</form>`} />
                </div>

                <h3>3. Input Validation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Client-side validation (user experience) --&gt;
<form>
    <input type="email" 
           name="email" 
           required 
           pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$">
    
    <input type="password" 
           name="password" 
           required 
           minlength="8" 
           pattern="(?=.*\\d)(?=.*[a-z])(?=.*[A-Z]).{8,}">
    
    <button type="submit">Submit</button>
</form>

<!-- ALWAYS validate on server too! Client-side can be bypassed --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Secure Links & Downloads</h2>

                <h3>1. External Links</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Always use rel="noopener noreferrer" with target="_blank" --&gt;
<a href="https://external-site.com" 
   target="_blank" 
   rel="noopener noreferrer">
    External Link
</a>

<!-- Why? Prevents window.opener exploitation --&gt;`} />
                </div>

                <h3>2. User-Generated Links</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Prevent javascript: URLs --&gt;
<a href="javascript:alert('XSS')">Click</a> <!-- Dangerous! --&gt;

<!-- Validate and sanitize URLs --&gt;
<script>
function isSafeURL(url) {
    try {
        const parsed = new URL(url);
        // Only allow http and https
        return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
        return false;
    }
}

// Use it
const userURL = getUserInput();
if (isSafeURL(userURL)) {
    link.href = userURL;
} else {
    console.error('Invalid URL');
}
</script>`} />
                </div>

                <h3>3. File Downloads</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Specify download attribute to prevent execution --&gt;
<a href="/files/document.pdf" download="report.pdf">Download Report</a>

<!-- Set correct Content-Type headers on server --&gt;
<!-- Set Content-Disposition: attachment --&gt;
<!-- Scan uploaded files for malware --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Security Headers</h2>
                <p>
                    HTTP headers that improve security (set on server):
                </p>

                <div className="headers-list">
                    <h3>1. Strict-Transport-Security (HSTS)</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Force HTTPS for specified time --&gt;
Strict-Transport-Security: max-age=31536000; includeSubDomains; preload`} />
                    </div>

                    <h3>2. X-Content-Type-Options</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Prevent MIME-sniffing --&gt;
X-Content-Type-Options: nosniff`} />
                    </div>

                    <h3>3. X-XSS-Protection</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Enable browser's XSS filter (legacy browsers) --&gt;
X-XSS-Protection: 1; mode=block`} />
                    </div>

                    <h3>4. Referrer-Policy</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Control referrer information --&gt;
Referrer-Policy: strict-origin-when-cross-origin

<meta name="referrer" content="strict-origin-when-cross-origin">`} />
                    </div>

                    <h3>5. Permissions-Policy</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Control browser features --&gt;
Permissions-Policy: geolocation=(), microphone=(), camera=()

<meta http-equiv="Permissions-Policy" 
      content="geolocation=(), microphone=(), camera=()">`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Subresource Integrity (SRI)</h2>
                <p>
                    Verify that files from CDNs haven't been tampered with:
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- SRI for external scripts --&gt;
<script src="https://cdn.example.com/library.js"
        integrity="sha384-hash-of-file-content"
        crossorigin="anonymous"></script>

<!-- SRI for external stylesheets --&gt;
<link rel="stylesheet" 
      href="https://cdn.example.com/style.css"
      integrity="sha384-hash-of-file-content"
      crossorigin="anonymous">

<!-- Generate SRI hash: https://www.srihash.org/ --&gt;`} />
                </div>

                <h3>Why Use SRI:</h3>
                <ul>
                    <li>Protects against compromised CDNs</li>
                    <li>Prevents malicious code injection</li>
                    <li>Ensures file integrity</li>
                    <li>Required for security-sensitive applications</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Sensitive Data Handling</h2>

                <h3>1. Never Store Sensitive Data Client-Side</h3>
                <div className="code-example error">
                    <div className="code-header">
                        <span> BAD - Don't Store These Client-Side</span>
                    </div>
                    <CodeBlock language="javascript" code={`// NEVER store these in localStorage, sessionStorage, or cookies!
localStorage.setItem('password', userPassword); // NO!
localStorage.setItem('creditCard', cardNumber); // NO!
localStorage.setItem('ssn', socialSecurity); // NO!
localStorage.setItem('apiKey', secretKey); // NO!`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>GOOD - Use Secure Server-Side Storage</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Store sensitive data server-side with encryption
// Use secure session tokens
const sessionToken = generateSecureToken();
localStorage.setItem('sessionToken', sessionToken); // OK (token only)

// Use HttpOnly cookies for auth (can't be accessed by JavaScript)
Set-Cookie: auth=token; HttpOnly; Secure; SameSite=Strict`} />
                </div>

                <h3>2. Clear Sensitive Data</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Clear sensitive form data after use
function clearSensitiveData() {
    document.getElementById('password').value = '';
    document.getElementById('creditCard').value = '';
}

// Clear on logout
function logout() {
    localStorage.clear();
    sessionStorage.clear();
    clearSensitiveData();
    window.location.href = '/login';
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Security Checklist</h2>
                <div className="checklist">
                    <h3>XSS Prevention:</h3>
                    <ul>
                        <li> Use <code>textContent</code> instead of <code>innerHTML</code></li>
                        <li> Sanitize user input with DOMPurify</li>
                        <li> Implement Content Security Policy</li>
                        <li> Validate and escape all user input</li>
                        <li> Use template engines with auto-escaping</li>
                    </ul>

                    <h3>CSRF Prevention:</h3>
                    <ul>
                        <li> Implement CSRF tokens</li>
                        <li> Use SameSite cookies</li>
                        <li> Verify Origin/Referer headers</li>
                        <li> Require re-authentication for sensitive actions</li>
                    </ul>

                    <h3>General Security:</h3>
                    <ul>
                        <li> Use HTTPS everywhere</li>
                        <li> Implement security headers</li>
                        <li> Use <code>rel="noopener noreferrer"</code> on external links</li>
                        <li> Validate all input (client AND server)</li>
                        <li> Use Subresource Integrity for CDN resources</li>
                        <li> Never store sensitive data client-side</li>
                        <li> Keep dependencies updated</li>
                        <li> Regular security audits</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Security Testing Tools</h2>
                <ul>
                    <li><strong>Mozilla Observatory:</strong> Test security headers</li>
                    <li><strong>Security Headers:</strong> Check header configuration</li>
                    <li><strong>OWASP ZAP:</strong> Automated vulnerability scanner</li>
                    <li><strong>Burp Suite:</strong> Web security testing platform</li>
                    <li><strong>npm audit:</strong> Check for vulnerable dependencies</li>
                    <li><strong>Lighthouse:</strong> Security audit in Chrome DevTools</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Always use HTTPS</li>
                            <li>Escape and sanitize user input</li>
                            <li>Implement CSP</li>
                            <li>Use security headers</li>
                            <li>Validate on client AND server</li>
                            <li>Use <code>rel="noopener"</code> on external links</li>
                            <li>Keep dependencies updated</li>
                            <li>Regular security audits</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Trust user input</li>
                            <li>Use <code>innerHTML</code> with user data</li>
                            <li>Store sensitive data client-side</li>
                            <li>Ignore security warnings</li>
                            <li>Use <code>eval()</code> or similar functions</li>
                            <li>Disable security features</li>
                            <li>Use deprecated security methods</li>
                            <li>Forget to update dependencies</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLSecurity;


